import React, { useCallback, useState } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import { useAutoSuggest } from '@uncinc/react-searchkit';

import { createSearchUrl } from '../../../helpers/createSearchUrl';
import SearchAutosuggest, { type SearchAutosuggestProps } from '../../Search/Autosuggest';
import SearchAllContentAutosuggestHit from '../AutosuggestHit';
import type { SearchAllContentHit } from '../types';
import {
  APP_OPTIONS,
  FAQ_BASE_PATH,
  NODE_URL_ALIAS_TEAM,
  SEARCH_PATH,
} from '../../../config/constants';

const SearchAllContentSearchAutosuggest: React.FC<Partial<SearchAutosuggestProps>> = (props) => {
  const [selectionMade, setSelectionMade] = useState<boolean>(false);
  const activeElement = document.activeElement as HTMLInputElement | undefined;
  const { t } = useTranslation();
  const history = useHistory();
  const { clearSearch } = useAutoSuggest();

  const {
    searchbarProps,
    autosuggestProps,
  } = props;

  const classNames = c('SearchAllContent__SearchBar');

  const onSearchSubmit = useCallback((query: string) => {
    if (selectionMade) {
      setSelectionMade(false);
      return;
    }

    const url = createSearchUrl({
      pathname: SEARCH_PATH,
      index: APP_OPTIONS.parameters.searchkitIndexAllContent,
      value: query,
    });

    try {
      history.push(url);
      activeElement?.blur();
      clearSearch();
    } catch (error) {
      console.error(error);
    }
  }, [activeElement, clearSearch, history, selectionMade]);

  return (
    <SearchAutosuggest
      {...props}
      autosuggestProps={{
        ...autosuggestProps,
        getSuggestionValue: (hit: SearchAllContentHit) => (
          // Page_title for nodes, name for taxonomy terms, colleague_full_name for profiles.
          hit.page_title?.[0] || hit.name?.[0] || hit.colleague_full_name?.[0]
        ) ?? '',
        renderSuggestion: (hit: SearchAllContentHit) => (
          <SearchAllContentAutosuggestHit hit={hit} />
        ),
        onSuggestionSelected: (e, { suggestion }: { suggestion: SearchAllContentHit }) => {
          setSelectionMade(true);

          if (suggestion.type?.[0] !== 'faq') {
            let url = suggestion.url[0];
            if (suggestion.objectID.includes('entity:profile')) {
              url = createSearchUrl({
                pathname: suggestion.colleague_team_url?.[0] || NODE_URL_ALIAS_TEAM,
                index: APP_OPTIONS.parameters.searchkitIndexTeam,
                value: suggestion.colleague_full_name?.[0] || '',
              });
            }
            history.push(url);
            activeElement?.blur();
            return;
          }

          const themePath = suggestion.theme_path?.[0];
          const link = themePath ? `${FAQ_BASE_PATH}${themePath}#${suggestion.uuid[0]}` : FAQ_BASE_PATH;
          history.replace(link);

          activeElement?.blur();
        },
      }}
      searchbarProps={{
        ...searchbarProps,
        onSubmit: onSearchSubmit,
        className: classNames,
        placeholder: t('components.search_all_content.search_bar.placeholder'),
        buttonText: t('buttons.search'),
      }}
    />
  );
};

export default SearchAllContentSearchAutosuggest;
